import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MultiSelectFilter from '../attivita/MultiSelectFilter';
import { clienteName, INTERNAL_CATEGORIES } from '../attivita/attivitaConfig';
import InactiveClients from './InactiveClients';
import { usePermissions } from '../../hooks/usePermissions';
import toast from 'react-hot-toast';
import { visiteAPI, clientsAPI, usersAPI } from '../../utils/api';
import { Cliente, VisitaStato } from '../../types';
import { useDashboardActivityTypes, filterDashboardActivities, resolveActivityType } from '../../hooks/useActivityTypes';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  addMonths,
  isSameMonth,
  differenceInCalendarDays,
} from 'date-fns';
import { it } from 'date-fns/locale';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, Line, Legend, Cell,
} from 'recharts';
import {
  CalendarDaysIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BellAlertIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

// --- Config ---------------------------------------------------------------

const STATO_META: Record<VisitaStato, { label: string; color: string }> = {
  programmata: { label: 'Programmate', color: '#F59E0B' },
  completata: { label: 'Completate', color: '#10B981' },
  annullata: { label: 'Annullate', color: '#EF4444' },
  rinviata: { label: 'Rinviate', color: '#F97316' },
};

const clienteDisplayName = (c?: Cliente): string => {
  if (!c) return 'Cliente';
  if (c.ragione_sociale) return c.ragione_sociale;
  const full = [c.nome, c.cognome].filter(Boolean).join(' ').trim();
  return full || c.nome || 'Cliente';
};

// --- Skeletons / empty ----------------------------------------------------

const SkeletonCard: React.FC = () => (
  <div className="bg-white p-6 rounded-lg shadow">
    <div className="flex items-center">
      <div className="animate-pulse bg-gray-200 h-8 w-8 rounded" />
      <div className="ml-4 space-y-2">
        <div className="animate-pulse bg-gray-200 h-4 w-20 rounded" />
        <div className="animate-pulse bg-gray-200 h-7 w-12 rounded" />
      </div>
    </div>
  </div>
);

const SkeletonChart: React.FC<{ height?: string }> = ({ height = 'h-64' }) => (
  <div className={`animate-pulse bg-gray-100 ${height} rounded`} />
);

const EmptyChart: React.FC<{ message?: string }> = ({ message = 'Nessun dato disponibile' }) => (
  <div className="flex items-center justify-center h-56 text-gray-400 text-sm">{message}</div>
);

// --- Breakdown (proportional bars) ----------------------------------------

const Breakdown: React.FC<{
  items: { label: string; count: number; color: string; value: string }[];
  onSelect?: (value: string) => void;
}> = ({ items, onSelect }) => {
  const total = items.reduce((s, i) => s + i.count, 0);
  if (total === 0) return <EmptyChart />;
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const pct = total > 0 ? (item.count / total) * 100 : 0;
        return (
          <div
            key={item.label}
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            onClick={onSelect ? () => onSelect(item.value) : undefined}
            onKeyDown={onSelect ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(item.value); } } : undefined}
            className={onSelect ? 'cursor-pointer hover:opacity-80' : undefined}
          >
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="flex items-center gap-2 text-gray-700">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                {item.label}
              </span>
              <span className="text-gray-900 font-medium">
                {item.count} <span className="text-gray-400 font-normal">({pct.toFixed(0)}%)</span>
              </span>
            </div>
            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: item.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Per-cliente bar chart (top / bottom) ----------------------------------

interface ClienteCount {
  clienteId: string;
  nome: string;
  count: number;
}

const ClienteBarChart: React.FC<{
  data: ClienteCount[];
  color: string;
  onSelect: (clienteId: string) => void;
}> = ({ data, color, onSelect }) => (
  <ResponsiveContainer width="100%" height={Math.max(200, data.length * 38)}>
    <BarChart data={data} layout="vertical" margin={{ left: 20, right: 30 }}>
      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
      <YAxis
        type="category"
        dataKey="nome"
        width={150}
        tick={{ fontSize: 12, fill: '#374151' }}
      />
      <Tooltip formatter={(value) => [value, 'Attività']} cursor={{ fill: '#F3F4F6' }} />
      <Bar
        dataKey="count"
        name="Attività"
        radius={[0, 4, 4, 0]}
        cursor="pointer"
        onClick={(_data: unknown, index: number) => {
          const entry = data[index];
          if (entry) onSelect(entry.clienteId);
        }}
      >
        {data.map((_entry, idx) => (
          <Cell key={idx} fill={color} />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

// --- Main component -------------------------------------------------------

const ActivityAnalytics: React.FC<{ fromDate: string; toDate: string }> = ({ fromDate, toDate }) => {
  const navigate = useNavigate();
  const today = React.useMemo(() => new Date(), []);
  const { hasPermission } = usePermissions();
  const [agentIds, setAgentIds] = React.useState<string[]>([]);
  const [states, setStates] = React.useState<string[]>([]);
  const [types, setTypes] = React.useState<string[]>([]);
  const [origin, setOrigin] = React.useState('');
  const [internal, setInternal] = React.useState('');
  const [exporting, setExporting] = React.useState(false);
  const filters = React.useMemo(() => ({ dashboard_only: true, from_date: fromDate, to_date: toDate,
    user_id: agentIds.length ? agentIds.join(',') : undefined,
    stato: states.length ? states.join(',') : undefined,
    tipo: types.length ? types.join(',') : undefined,
    origine: origin ? origin as 'app' | 'manuale' : undefined,
    interne: internal ? internal === 'true' : undefined,
  }), [fromDate, toDate, agentIds, states, types, origin, internal]);
  const { data: agents } = useQuery({ queryKey: ['users-hierarchy'], queryFn: async () => (await usersAPI.getHierarchy()).data });
  const activityLink = (extra: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    Object.entries({ ...filters, view: 'grid', reference_date: format(today, 'yyyy-MM-dd'), ...extra }).forEach(([key, value]) => { if (value !== undefined) params.set(key, String(value)); });
    return `/attivita?${params}`;
  };
  const exportActivities = async () => {
    setExporting(true);
    try {
      const response = await visiteAPI.exportVisite(filters);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a'); link.href = url; link.download = 'attivita-dashboard.xlsx'; link.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Errore nell’esportazione delle attività'); }
    finally { setExporting(false); }
  };

  // Finestra di analisi decisa dal filtro periodo della dashboard (default
  // ultimi 30 giorni, clamp a 36 mesi lato pagina).
  const fromStr = fromDate;
  const toStr = toDate;

  // The bounded calendar endpoint returns the complete filtered window.
  const { data: visitsData, isLoading: isLoadingVisits, isError: visitsError } = useQuery({
    queryKey: ['activity-analytics', 'visite', filters],
    queryFn: async () => ({ visits: (await visiteAPI.getCalendar(filters)).data }),
    staleTime: 5 * 60 * 1000,
  });

  // Client name lookup (visits only carry cliente_id).
  const { data: clientsList } = useQuery({
    queryKey: ['clienti', 'select'],
    queryFn: async () => (await clientsAPI.getClientsForSelect()).data,
    staleTime: 5 * 60 * 1000,
  });

  // Tipologie attività dall'anagrafica per tutti i contesti: la distribuzione
  // "Per Tipo" e le icone in agenda seguono i tipi definiti a sistema, non una
  // lista statica.
  const { data: activityTypes } = useDashboardActivityTypes();

  const clientNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (clientsList || []).forEach((c) => m.set(c.id, clienteDisplayName(c)));
    return m;
  }, [clientsList]);

  const visits = React.useMemo(
    () => filterDashboardActivities(visitsData?.visits || [], activityTypes),
    [visitsData, activityTypes],
  );

  // --- Derived analytics ---
  const kpis = React.useMemo(() => {
    let prossime7 = 0;
    let oggi = 0;
    let completateMese = 0;
    let inRitardo = 0;
    let followUp7 = 0;
    let completate = 0;
    let annullate = 0;

    visits.forEach((v) => {
      const diff = differenceInCalendarDays(parseISO(v.data), today);
      if (v.stato === 'programmata') {
        if (diff === 0) oggi += 1;
        if (diff >= 0 && diff <= 7) prossime7 += 1;
        if (diff < 0) inRitardo += 1;
      }
      if (v.stato === 'completata') {
        completate += 1;
        if (isSameMonth(parseISO(v.data), today)) completateMese += 1;
      }
      if (v.stato === 'annullata') annullate += 1;
      if (v.follow_up_date) {
        const fdiff = differenceInCalendarDays(parseISO(v.follow_up_date), today);
        if (fdiff >= 0 && fdiff <= 7) followUp7 += 1;
      }
    });

    const chiuse = completate + annullate;
    const tasso = chiuse > 0 ? (completate / chiuse) * 100 : 0;

    return { prossime7, oggi, completateMese, inRitardo, followUp7, tasso };
  }, [visits, today]);

  // Agenda: programmate recenti-in-ritardo + future, ordinate per data.
  // Solo attività legate a un cliente: quelle su offerta non hanno una scheda cliente da aprire.
  const agenda = React.useMemo(() => {
    return visits
      .filter((v) => v.stato === 'programmata' && differenceInCalendarDays(parseISO(v.data), today) >= -14)
      .sort((a, b) => a.data.localeCompare(b.data) || (a.ora || '').localeCompare(b.ora || ''))
      .slice(0, 10);
  }, [visits, today]);

  // Attività per cliente, ordinate per conteggio decrescente. Esclude le attività
  // legate a un'offerta (nessun cliente_id) e quelle Annullate (non rappresentano
  // lavoro effettivo sul cliente). Da qui derivano Top 10 e "meno contattati".
  const perClienteAll = React.useMemo((): ClienteCount[] => {
    const counts = new Map<string, number>();
    visits.forEach((v) => {
      if (!v.cliente_id) return;
      if (v.stato === 'annullata') return;
      counts.set(v.cliente_id, (counts.get(v.cliente_id) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([clienteId, count]) => ({
        clienteId,
        nome: clientNameById.get(clienteId) || 'Cliente',
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [visits, clientNameById]);

  const perCliente = React.useMemo(() => perClienteAll.slice(0, 10), [perClienteAll]);

  // Clienti con meno attività (tra quelli con almeno una attività nel periodo).
  const perClienteMeno = React.useMemo(
    () => [...perClienteAll].sort((a, b) => a.count - b.count).slice(0, 10),
    [perClienteAll],
  );

  // Trend mensile sulla finestra selezionata: totale + completate.
  const trendData = React.useMemo(() => {
    const months: { label: string; totale: number; completate: number; from_date: string; to_date: string }[] = [];
    let d = startOfMonth(parseISO(fromStr));
    const last = startOfMonth(parseISO(toStr));
    // Guard: la finestra è clampata a 36 mesi dalla dashboard, ma non si sa mai.
    for (let i = 0; i < 48 && d <= last; i += 1) {
      const label = format(d, 'MMM yy', { locale: it });
      const totale = visits.filter((v) => isSameMonth(parseISO(v.data), d)).length;
      const completate = visits.filter((v) => v.stato === 'completata' && isSameMonth(parseISO(v.data), d)).length;
      months.push({
        label, totale, completate,
        from_date: [fromStr, format(d, 'yyyy-MM-dd')].sort()[1],
        to_date: [toStr, format(endOfMonth(d), 'yyyy-MM-dd')].sort()[0],
      });
      d = addMonths(d, 1);
    }
    return months;
  }, [visits, fromStr, toStr]);

  // Distribuzioni per tipo / stato, ordinate per conteggio decrescente. `value` porta
  // il codice grezzo (non l'etichetta localizzata) così il click naviga a un filtro
  // valido indipendentemente dalla lingua. I tipi vengono dall'anagrafica
  // activity_types; eventuali codici presenti nelle attività ma non (più) in
  // anagrafica sono accodati per non perdere il dato storico.
  const perTipo = React.useMemo(() => {
    const counts = new Map<string, number>();
    visits.forEach((v) => counts.set(v.tipo, (counts.get(v.tipo) || 0) + 1));
    const known = new Set((activityTypes || []).map((t) => t.codice));
    const codici = [
      ...(activityTypes || []).filter((t) => t.attivo_dashboard !== false).map((t) => t.codice),
      ...Array.from(counts.keys()).filter((c) => !known.has(c)),
    ];
    return codici
      .map((codice) => {
        const meta = resolveActivityType(codice, activityTypes);
        return { label: meta.label, count: counts.get(codice) || 0, color: meta.colore, value: codice };
      })
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [visits, activityTypes]);

  const perStato = React.useMemo(() => {
    return (['programmata', 'completata', 'annullata', 'rinviata'] as VisitaStato[])
      .map((s) => ({ label: STATO_META[s].label, count: visits.filter((v) => v.stato === s).length, color: STATO_META[s].color, value: s }))
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [visits]);

  const hasAnyVisit = visits.length > 0;

  const kpiCards = [
    {
      label: 'Prossime 7 giorni', kpi: 'prossime7',
      value: kpis.prossime7,
      icon: <CalendarDaysIcon className="h-8 w-8 text-blue-400" />,
    },
    {
      label: 'Oggi', kpi: 'oggi',
      value: kpis.oggi,
      icon: <CalendarIcon className="h-8 w-8 text-indigo-400" />,
    },
    {
      label: 'Completate (mese)', kpi: 'completate_mese',
      value: kpis.completateMese,
      icon: <CheckCircleIcon className="h-8 w-8 text-green-400" />,
    },
    {
      label: 'In ritardo', kpi: 'in_ritardo',
      value: kpis.inRitardo,
      icon: <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />,
      danger: kpis.inRitardo > 0,
    },
    {
      label: 'Follow-up in scadenza', kpi: 'follow_up',
      value: kpis.followUp7,
      icon: <BellAlertIcon className="h-8 w-8 text-amber-400" />,
    },
    {
      label: 'Tasso completamento', kpi: 'tasso',
      value: `${kpis.tasso.toFixed(0)}%`,
      icon: <ArrowTrendingUpIcon className="h-8 w-8 text-emerald-400" />,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 rounded-lg bg-white p-4 shadow sm:grid-cols-3">
        <MultiSelectFilter label="Venditore" allLabel="Tutti i venditori" selected={agentIds} onChange={setAgentIds} options={(agents || []).filter((u) => u.is_active && u.role !== 'cliente').map((u) => ({ value: u.id, label: u.name || `${u.nome} ${u.cognome}` }))} />
        <MultiSelectFilter label="Stato" allLabel="Tutti gli stati" selected={states} onChange={setStates} options={Object.entries(STATO_META).map(([value, meta]) => ({ value, label: meta.label }))} />
        <MultiSelectFilter label="Tipo" allLabel="Tutti i tipi" selected={types} onChange={setTypes} options={[...(activityTypes || []).filter((t) => !t.is_system).map((t) => ({ value: t.codice, label: t.label })), ...Object.entries(INTERNAL_CATEGORIES).filter(([code]) => !(activityTypes || []).some((t) => t.codice === code)).map(([value, label]) => ({ value, label }))]} />
        <label className="text-sm">Origine<select aria-label="Origine dashboard" value={origin} onChange={(e) => setOrigin(e.target.value)} className="mt-1 w-full rounded-lg border p-2"><option value="">Tutte</option><option value="manuale">Manuale</option><option value="app">Richieste da app</option></select></label>
        <label className="text-sm">Ambito<select aria-label="Ambito dashboard" value={internal} onChange={(e) => setInternal(e.target.value)} className="mt-1 w-full rounded-lg border p-2"><option value="">Tutte le attività</option><option value="false">Commerciali</option><option value="true">Interne</option></select></label>
        {hasPermission('visite:export') && <button disabled={exporting} onClick={exportActivities} className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white">{exporting ? 'Esportazione…' : 'Esporta Excel'}</button>}
      </div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {isLoadingVisits ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          kpiCards.map((card) => (
            <button type="button" key={card.label} onClick={() => navigate(activityLink({ kpi: card.kpi }))} title={card.kpi === 'tasso' ? 'Apri attività completate e annullate' : 'Apri le attività conteggiate'} disabled={visitsError} className="bg-white p-6 rounded-lg shadow text-left hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500">
              <div className="flex items-center">
                <div className="flex-shrink-0">{card.icon}</div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{card.label}</p>
                  <p className={`text-2xl font-semibold ${card.danger ? 'text-red-600' : 'text-gray-900'}`}>
                    {card.value}
                  </p>
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {visitsError && <p role="alert" className="text-red-600">Impossibile caricare le attività.</p>}
      <InactiveClients key={agentIds.join(',')} agentIds={agentIds} />
      <section className="rounded-lg bg-white p-6 shadow">
        <h3 className="mb-4 text-lg font-medium">Richieste da app per stato</h3>
        <Breakdown items={(Object.keys(STATO_META) as VisitaStato[]).map((state) => ({ value: state, label: STATO_META[state].label, color: STATO_META[state].color, count: visits.filter((visit) => visit.origine === 'app' && visit.stato === state).length }))} onSelect={(state) => navigate(activityLink({ origine: 'app', stato: state }))} />
      </section>

      {/* Empty state (no data at all) */}
      {!isLoadingVisits && !hasAnyVisit ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <CalendarDaysIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600 font-medium">Nessuna attività registrata</p>
          <p className="text-sm text-gray-400 mt-1">
            Pianifica visite, telefonate e videochiamate dalla scheda cliente, tab &laquo;Visite &amp; Attività&raquo;.
          </p>
        </div>
      ) : (
        <>
          {/* Trend mensile */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Andamento Attività</h3>
            {isLoadingVisits ? (
              <SkeletonChart height="h-72" />
            ) : trendData.every((m) => m.totale === 0) ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={trendData} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value, name) => [value, name === 'totale' ? 'Totale attività' : 'Completate']}
                  />
                  <Legend formatter={(value: string) => (value === 'totale' ? 'Totale attività' : 'Completate')} />
                  <Bar dataKey="totale" fill="#3B82F6" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(_data: unknown, index: number) => { const month = trendData[index]; if (month) navigate(activityLink({ from_date: month.from_date, to_date: month.to_date })); }} />
                  <Line dataKey="completate" stroke="#10B981" strokeWidth={2} dot={(props: { cx?: number; cy?: number; index?: number }) => {
                    const month = trendData[props.index ?? -1];
                    const open = () => { if (month) navigate(activityLink({ from_date: month.from_date, to_date: month.to_date, kpi: 'completate' })); };
                    return <circle key={props.index} cx={props.cx} cy={props.cy} r={5} fill="#10B981" role="button" tabIndex={0} aria-label={`Completate ${month?.label || ''}`} onClick={open} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(); } }} style={{ cursor: 'pointer' }} />;
                  }} activeDot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Agenda + Distribuzioni */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Agenda prossime attività */}
            <div className="lg:col-span-3 bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Prossime Attività</h3>
              {isLoadingVisits ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="animate-pulse bg-gray-100 h-14 rounded" />
                  ))}
                </div>
              ) : agenda.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
                  Nessuna attività programmata
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {agenda.map((v) => {
                    const tipo = resolveActivityType(v.tipo, activityTypes);
                    const TipoIcon = tipo.icon;
                    const diff = differenceInCalendarDays(parseISO(v.data), today);
                    const isLate = diff < 0;
                    let dataLabel: string;
                    try {
                      dataLabel = format(parseISO(v.data), 'EEE d MMM', { locale: it });
                    } catch {
                      dataLabel = v.data;
                    }
                    return (
                      <button
                        key={v.id}
                        onClick={() => navigate(v.cliente_id ? `/clients/${v.cliente_id}` : activityLink({ user_id: v.user_id, from_date: v.data, to_date: v.data }))}
                        className="w-full flex items-center gap-3 py-3 text-left hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
                      >
                        <span className="flex-shrink-0 p-2 rounded-lg" style={{ backgroundColor: `${tipo.colore}1A`, color: tipo.colore }}>
                          <TipoIcon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {v.cliente_id ? clientNameById.get(v.cliente_id) || clienteName(v) : v.categoria_interna ? clienteName(v) : 'Attività su offerta'}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-2">
                            <span>{tipo.label}</span>
                            {v.ora && (
                              <span className="inline-flex items-center gap-0.5">
                                <ClockIcon className="h-3 w-3" />
                                {v.ora.substring(0, 5)}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className={`text-sm font-medium capitalize ${isLate ? 'text-red-600' : 'text-gray-700'}`}>
                            {dataLabel}
                          </p>
                          {isLate && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              In ritardo
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Distribuzioni */}
            <div className="lg:col-span-2 bg-white shadow rounded-lg p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Per Tipo</h3>
                {isLoadingVisits ? <SkeletonChart height="h-24" /> : (
                  <Breakdown items={perTipo} onSelect={(v) => navigate(activityLink({ tipo: v }))} />
                )}
              </div>
              <div className="border-t border-gray-100 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Per Stato</h3>
                {isLoadingVisits ? <SkeletonChart height="h-32" /> : (
                  <Breakdown items={perStato} onSelect={(v) => navigate(activityLink({ stato: v }))} />
                )}
              </div>
            </div>
          </div>

          {/* Attività per cliente: più e meno contattati */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Attività per Cliente (Top 10)</h3>
              {isLoadingVisits ? (
                <SkeletonChart height="h-72" />
              ) : perCliente.length === 0 ? (
                <EmptyChart />
              ) : (
                <ClienteBarChart
                  data={perCliente}
                  color="#3B82F6"
                  onSelect={(clienteId) => navigate(activityLink({ cliente_id: clienteId, exclude_cancelled: 'true' }))}
                />
              )}
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Clienti meno contattati (Bottom 10)</h3>
              {isLoadingVisits ? (
                <SkeletonChart height="h-72" />
              ) : perClienteMeno.length === 0 ? (
                <EmptyChart />
              ) : (
                <ClienteBarChart
                  data={perClienteMeno}
                  color="#F59E0B"
                  onSelect={(clienteId) => navigate(activityLink({ cliente_id: clienteId, exclude_cancelled: 'true' }))}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityAnalytics;
